import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => d.toISOString().slice(0, 10);
const prettyDT = (iso, locale) => {
  try { return new Date(iso).toLocaleString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const TemplateCard = ({ t }) => (
  <button type="button" onClick={() => window.open(`${API_URL}${t.url}`, '_blank', 'noopener')}
     className="w-full flex items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 p-3 hover:border-orange-300 hover:shadow-sm transition text-left"
     data-testid={`template-${t.key}`}>
    <div>
      <p className="text-sm font-semibold text-slate-800">{t.label}</p>
      <p className="text-[11px] text-slate-400">{t.desc}</p>
    </div>
    <span className="text-xs font-semibold text-orange-600 whitespace-nowrap">⬇ CSV</span>
  </button>
);

export default function OnboardingScheduler({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const locale = language === 'hi' ? 'hi-IN' : 'en-IN';
  const dayLabel = (d) => d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short' });
  const TEMPLATES = [
    { key: 'school_profile', label: L('🏫 School Profile (UDISE+)', '🏫 School Profile (UDISE+)'), url: '/api/bulk/template/school-profile/download', desc: L('All fields in UDISE+ order', 'सभी fields UDISE+ क्रम में') },
    { key: 'students_basic', label: L('👨‍🎓 Students — Basic', '👨‍🎓 विद्यार्थी — Basic'), url: '/api/bulk/template/basic/download', desc: L('Essential 17 fields', 'ज़रूरी 17 fields') },
    { key: 'students_full', label: L('👨‍🎓 Students — Full (Govt)', '👨‍🎓 विद्यार्थी — पूरा (सरकारी)'), url: '/api/bulk/template/full/download', desc: L('All 55 government fields', 'सभी 55 सरकारी fields') },
    { key: 'teachers', label: L('👨‍🏫 Teachers/Staff', '👨‍🏫 शिक्षक'), url: '/api/bulk/template/teachers/download', desc: L('Employee ID, subject, qualification', 'Employee ID, विषय, qualification') },
    { key: 'classes', label: L('🏫 Classes & Sections', '🏫 कक्षाएँ'), url: '/api/bulk/template/classes/download', desc: L('Class, section, capacity', 'कक्षा, सेक्शन, capacity') },
  ];
  const authToken = token || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

  const today = new Date();
  // Next 21 calendar days, weekdays only (Sat/Sun off)
  const dates = Array.from({ length: 21 }, (_, i) => { const d = new Date(today); d.setDate(d.getDate() + i); return d; })
    .filter((d) => d.getDay() !== 0 && d.getDay() !== 6)
    .slice(0, 12);

  const [selDate, setSelDate] = useState(fmtDate(dates[0]));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [booking, setBooking] = useState(false);
  const [mySchedule, setMySchedule] = useState(null);
  const [rescheduleMode, setRescheduleMode] = useState(false);

  const fetchMine = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding-schedule/my`, { headers });
      const d = await res.json();
      const active = (d.schedules || []).find((s) => ['scheduled', 'rescheduled'].includes(s.status));
      setMySchedule(active || null);
    } catch (e) { /* noop */ }
  }, [authToken]);

  const fetchSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding-schedule/slots?date=${date}`, { headers });
      const d = await res.json();
      setSlots(d.slots || []);
    } catch (e) { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, [authToken]);

  useEffect(() => { fetchMine(); }, [fetchMine]);
  useEffect(() => { fetchSlots(selDate); }, [selDate, fetchSlots]);

  const book = async (startTime) => {
    setBooking(true);
    try {
      const isResch = rescheduleMode && mySchedule;
      const endpoint = isResch
        ? `${API_URL}/api/onboarding-schedule/${mySchedule.id}/reschedule`
        : `${API_URL}/api/onboarding-schedule/book`;
      const res = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ date: selDate, start_time: startTime }) });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || L('Could not book slot', 'Slot book नहीं हो पाया')); }
      else {
        toast.success(isResch ? L('🔄 Rescheduled!', '🔄 Reschedule हो गया!') : L('✅ Onboarding locked!', '✅ Onboarding lock हो गया!'));
        setRescheduleMode(false);
        await fetchMine();
        await fetchSlots(selDate);
      }
    } catch (e) { toast.error(L('Network error', 'Network error')); }
    finally { setBooking(false); }
  };

  const cancel = async () => {
    if (!mySchedule || !window.confirm(L('Do you want to cancel this onboarding?', 'क्या आप इस onboarding को cancel करना चाहते हैं?'))) return;
    try {
      const res = await fetch(`${API_URL}/api/onboarding-schedule/${mySchedule.id}/cancel`, { method: 'POST', headers });
      if (res.ok) { toast.success(L('Cancelled', 'Cancel हो गया')); setMySchedule(null); fetchSlots(selDate); }
    } catch (e) { toast.error(L('Could not cancel', 'Cancel नहीं हो पाया')); }
  };

  const canReschedule = mySchedule && (new Date(mySchedule.start_iso).getTime() - Date.now()) > 3600 * 1000;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6" data-testid="onboarding-scheduler-page">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
        <h1 className="text-xl font-bold">🚀 {L('Onboarding & Data Upload', 'Onboarding & डेटा अपलोड')}</h1>
        <p className="text-sm text-white/90 mt-1">{L('Our Technical Expert will help upload your real data to the Shiksha Mitra portal — in a 30-min Google Meet call.', 'हमारा Technical Expert आपका real data शिक्षा मित्र portal पर upload करने में मदद करेगा — एक 30-min Google Meet call में।')}</p>
      </div>

      {/* My active schedule */}
      {mySchedule && !rescheduleMode && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5" data-testid="my-onboarding-card">
          <p className="text-sm font-bold text-green-800">{L('✅ Your Onboarding is locked', '✅ आपका Onboarding lock है')}</p>
          <p className="text-2xl font-black text-green-700 mt-1">{prettyDT(mySchedule.start_iso, locale)}</p>
          <p className="text-xs text-green-600 mt-0.5">{L('IST · 30 minutes', 'IST · 30 मिनट')}</p>
          {mySchedule.meet_link ? (
            <a href={mySchedule.meet_link} target="_blank" rel="noopener noreferrer"
               className="inline-block mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700" data-testid="meet-link">
              🔗 {L('Join Google Meet', 'Google Meet जॉइन करें')}
            </a>
          ) : (
            <p className="text-xs text-amber-600 mt-3" data-testid="meet-pending">{L('⏳ Meet link is being prepared — you will get it here and on WhatsApp before the call.', '⏳ Meet link तैयार हो रहा है — call से पहले यहाँ और WhatsApp पर मिल जाएगा।')}</p>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={() => { setRescheduleMode(true); }} disabled={!canReschedule}
              className="text-xs px-3 py-1.5 rounded-lg border border-green-300 text-green-700 disabled:opacity-40" data-testid="reschedule-btn">
              🔄 {L('Reschedule', 'Reschedule')} {(!canReschedule) && L('(up to 1 hour before)', '(1 घंटे पहले तक)')}
            </button>
            <button onClick={cancel} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600" data-testid="cancel-btn">{L('Cancel', 'रद्द करें')}</button>
          </div>
        </div>
      )}

      {/* Scheduler (show if no active schedule OR rescheduling) */}
      {(!mySchedule || rescheduleMode) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="slot-picker">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-slate-800">{rescheduleMode ? L('🔄 Pick a new time', '🔄 नया समय चुनें') : L('📅 Pick your time', '📅 अपना समय चुनें')}</p>
            {rescheduleMode && <button onClick={() => setRescheduleMode(false)} className="text-xs text-slate-400">{L('Cancel', 'रद्द')}</button>}
          </div>

          {/* Date strip */}
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="date-strip">
            {dates.map((d) => {
              const ds = fmtDate(d);
              return (
                <button key={ds} onClick={() => setSelDate(ds)}
                  className={`shrink-0 px-3 py-2 rounded-xl text-xs font-medium border ${selDate === ds ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'}`}
                  data-testid={`date-${ds}`}>
                  {dayLabel(d)}
                </button>
              );
            })}
          </div>

          {/* Slots */}
          <p className="text-[11px] text-slate-400 mt-3 mb-2">{L('Time (Mon–Fri · 10:00 AM – 6:00 PM IST · 30 min)', 'समय (सोम–शुक्र · 10:00 AM – 6:00 PM IST · 30 मिनट)')}</p>
          {loadingSlots ? (
            <p className="text-center text-slate-400 text-sm py-6">⏳ {L('loading slots...', 'slots लोड हो रहे हैं...')}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-grid">
              {slots.map((s) => (
                <button key={s.start_time} disabled={!s.available || booking}
                  onClick={() => book(s.start_time)}
                  className={`py-2 rounded-lg text-sm font-medium border transition ${s.available ? 'bg-white text-slate-700 border-slate-200 hover:border-orange-400 hover:bg-orange-50' : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through'}`}
                  data-testid={`slot-${s.start_time}`} title={s.reason === 'booked' ? L('already booked', 'पहले से book') : s.reason === 'past' ? L('past', 'बीत चुका') : ''}>
                  {s.start_time}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sample data templates */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="sample-templates">
        <p className="text-sm font-bold text-slate-800">📥 {L('Sample Data Templates', 'Sample Data Templates')}</p>
        <p className="text-xs text-slate-400 mt-0.5 mb-3">{L('Download these templates and fill your real data — it gets uploaded instantly during the call.', 'ये templates download करके अपना असली डेटा भरें — call में तुरंत upload हो जाएगा।')}</p>
        <div className="space-y-2">
          {TEMPLATES.map((t) => <TemplateCard key={t.key} t={t} />)}
        </div>
      </div>
    </div>
  );
}
